import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroupConfig } from "@/components/feature/FilterPanel";
import SortBar from "@/components/feature/SortBar";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { videos } from "@/mocks/videos";
import type { SortKey, VideoFilters } from "@/types/video";
import {
  countActiveFilters,
  emptyFilters,
  filterVideos,
  getFilterOptions,
  searchVideos,
  sortVideos,
  toggleValue,
  yearOptions,
} from "@/utils/video";

const PAGE_SIZE = 12;

const categoryFilters = [
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "variety", label: "综艺" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(query);
  const [filters, setFilters] = useState<VideoFilters>(emptyFilters);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("popular");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<"page" | "more">("page");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setKeyword(query);
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [query]);

  useEffect(() => {
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [filters, categoryFilter, sortKey, viewMode]);

  useEffect(() => {
    document.title = query ? `搜索“${query}” - 91看片` : "搜索 - 91看片";
  }, [query]);

  const groups: FilterGroupConfig[] = useMemo(() => {
    const opts = getFilterOptions("movie");
    const allGenres = Array.from(new Set([...opts.genres, ...getFilterOptions("tv").genres, ...getFilterOptions("variety").genres]));
    const allRegions = Array.from(new Set([...opts.regions, ...getFilterOptions("tv").regions, ...getFilterOptions("variety").regions]));
    return [
      { key: "genres", label: "类型", options: allGenres },
      { key: "yearRange", label: "年份", options: [] },
      { key: "regions", label: "地区", options: allRegions },
    ];
  }, []);

  const matched = useMemo(() => (query ? searchVideos(videos, query) : []), [query]);

  const result = useMemo(() => {
    const scoped = categoryFilter.length > 0
      ? matched.filter((v) => categoryFilter.includes(v.category))
      : matched;
    return sortVideos(filterVideos(scoped, filters), sortKey);
  }, [matched, categoryFilter, filters, sortKey]);

  const hotList = useMemo(() => sortVideos(videos, "popular").slice(0, 5), []);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  const resetKeyword = () => {
    setKeyword("");
    setSearchParams({});
    setFilters(emptyFilters);
    setCategoryFilter([]);
  };

  const activeCount = countActiveFilters(filters) + categoryFilter.length;
  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const shownItems =
    viewMode === "page"
      ? result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : result.slice(0, visibleCount);

  const sidebarPanel = (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 font-heading text-sm font-bold text-foreground-900">内容分类</h3>
        <div className="flex flex-wrap gap-2">
          {categoryFilters.map((cat) => {
            const active = categoryFilter.includes(cat.value);
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategoryFilter((prev) => toggleValue(prev, cat.value))}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary-600 bg-primary-600 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>
      <FilterPanel
        groups={groups}
        filters={filters}
        yearOptions={yearOptions}
        onChange={setFilters}
        onReset={() => {
          setFilters(emptyFilters);
          setCategoryFilter([]);
        }}
      />
    </div>
  );

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

        <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            搜索影视
          </h1>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row" role="search">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入影视名称或主演关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                aria-label="搜索影视"
              />
            </div>
            <button
              type="submit"
              className="flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
          {query && (
            <p className="mt-3 text-sm text-foreground-600">
              关键词：<span className="font-bold text-primary-700">{query}</span>
              　共找到 <span className="font-bold text-accent-600">{result.length}</span> 个结果
            </p>
          )}
        </div>

        {!query ? (
          <div className="mt-8">
            <EmptyState
              icon="ri-search-2-line"
              title="输入关键词开始搜索"
              description="支持影视名称与主演关键词的模糊匹配，例如“流浪地球”“秦昊”“悬疑”。"
            />
            <section className="mt-10">
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {hotList.map((item) => (
                  <VideoCard key={item.id} item={item} showFavorite />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6 lg:flex-row">
            <aside className="hidden w-64 shrink-0 lg:block">
              <div className="sticky top-20 rounded-lg border border-background-200 bg-background-100 p-5">
                {sidebarPanel}
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="mb-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选条件
                {activeCount > 0 && (
                  <span className="rounded-full bg-accent-500 px-2 text-xs font-bold text-background-50">{activeCount}</span>
                )}
              </button>

              <SortBar
                sortKey={sortKey}
                onChange={setSortKey}
                total={result.length}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />

              <div className="mt-5">
                {result.length === 0 ? (
                  <div>
                    <EmptyState
                      icon="ri-emotion-sad-line"
                      title="没有找到相关影视"
                      description={`没有与“${query}”匹配的内容，换个关键词，或从热门推荐里挑一部吧。`}
                    >
                      <button
                        type="button"
                        onClick={resetKeyword}
                        className="cursor-pointer rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50"
                      >
                        重置关键词
                      </button>
                    </EmptyState>
                    <section className="mt-10">
                      <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {hotList.map((item) => (
                          <VideoCard key={item.id} item={item} showFavorite />
                        ))}
                      </div>
                    </section>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                      {shownItems.map((item) => (
                        <VideoCard key={item.id} item={item} showFavorite />
                      ))}
                    </div>

                    {viewMode === "page" ? (
                      <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={(next) => {
                          setPage(next);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      />
                    ) : (
                      <div className="mt-8 flex justify-center">
                        {visibleCount < result.length ? (
                          <button
                            type="button"
                            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                          >
                            <i className="ri-add-line" />
                            加载更多
                          </button>
                        ) : (
                          <p className="text-sm text-foreground-400">已经到底啦</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-pointer bg-primary-950/50"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-background-100 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-200"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {sidebarPanel}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full cursor-pointer rounded-md bg-primary-600 px-4 py-3 text-sm font-semibold text-background-50"
            >
              查看 {result.length} 个结果
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}